import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tabs,
  Tab,
  TablePagination,
  CircularProgress,
  LinearProgress,
  FormControl,
  Collapse,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { columnsOpportunities } from '../../constants/common';
import { useNavigate } from 'react-router-dom';
import ViewIcon from '../../assets/images/View.svg';
import CheckMarkGreen from '../../assets/images/CheckMarkGreen.svg';
import Cancel from '../../assets/images/Cancel.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from "react-toastify";
import { getOpportunities, getOpportunitiesSummary, updateOpportunityStatus, getContractWorkspaces } from '../../api/opportunities';
import SearchableDropdown from '../../components/SearchableDropdown/SeachableDropdown';

const StyledCard = styled(Card)(({ theme }) => ({
  minWidth: 200,
  minHeight: 120,
  margin: theme.spacing(1),
}));

interface Opportunity {
  id: number;
  opportunity_name: string;
  client_name: string;
  status: string;
  value: string;
  created_date: string;
  actions?: React.ReactNode;
}

// Mock data for opportunities - replace with actual API call
// const mockOpportunities: Opportunity[] = [
//   {
//     id: 1,
//     opportunity_name: "Commodity Index Opportunity",
//     client_name: "Flora Essentials",
//     status: "New",
//     value: "$130,469.50",
//     created_date: "1 day ago",
//   },
//   {
//     id: 2,
//     opportunity_name: "Too short payment targets compared to contract",
//     client_name: "Flora Essentials",
//     status: "New",
//     value: "-",
//     created_date: "1 day ago",
//   },
// ];

const Opportunities: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [selected, setSelected] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({
    openOpportunities: 0,
    closedOpportunities: 0,
    totalValueOpenOpps: '$0',
    totalValueRecovered: '-'
  });
  const [loading, setLoading] = useState(true);
  const [contractWorkspaces, setContractWorkspaces] = useState<Array<{id: string, label: string}>>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Array<{id: string, label: string}>>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      
      // Convert selected contract workspace IDs for API call
      const contractIds = selectedWorkspaces.length > 0 
        ? selectedWorkspaces.map(ws => parseInt(ws.id))
        : undefined;
      
      const data = await getOpportunities(page + 1, rowsPerPage, tabValue, contractIds);
      
      // Ensure we have a valid response with items before updating state
      if (data && data.items) {
        setOpportunities(data.items || []);
        setTotalItems(data.total || 0);
      } else {
        setOpportunities([]);
        setTotalItems(0);
      }
    } catch (error) {
      toast.error('Failed to fetch opportunities');
      console.error('Error fetching opportunities:', error);
      setOpportunities([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const summaryData = await getOpportunitiesSummary();
      setStats(summaryData);
    } catch (error) {
      toast.error('Failed to fetch summary data');
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    if (filtersInitialized) {
      fetchOpportunities();
      fetchStats();
    }
  }, [page, rowsPerPage, tabValue, selectedWorkspaces, filtersInitialized]);
  
  // Load contract workspaces for dropdown and check URL parameters
  useEffect(() => {
    const fetchContractWorkspaces = async () => {
      try {
        // Ensure loading state is true during initialization
        setLoading(true);
        
        const workspaces = await getContractWorkspaces();
        setContractWorkspaces(workspaces);
        
        // Check URL for contract_ids or contract_id parameter
        const searchParams = new URLSearchParams(window.location.search);
        const contractIdsParam = searchParams.get('contract_ids');
        const contractIdParam = searchParams.get('contract_id');
        
        if (contractIdsParam) {
          // Handle multiple contract IDs
          const contractIds = contractIdsParam.split(',');
          const selectedWorkspacesFromURL = workspaces.filter(ws => contractIds.includes(ws.id));
          if (selectedWorkspacesFromURL.length > 0) {
            setSelectedWorkspaces(selectedWorkspacesFromURL);
          }
        } else if (contractIdParam) {
          // Handle single contract ID
          const selectedWorkspace = workspaces.find(ws => ws.id === contractIdParam);
          if (selectedWorkspace) {
            setSelectedWorkspaces([selectedWorkspace]);
          }
        }
        
        // Mark filters as initialized to trigger data loading
        setFiltersInitialized(true);
        
        // Note: We don't set loading to false here because fetchOpportunities will be called
        // after filtersInitialized is set to true, and it will handle the loading state
      } catch (error) {
        console.error('Error fetching contract workspaces:', error);
        // If there's an error, we should still set loading to false
        setLoading(false);
      }
    };
    
    fetchContractWorkspaces();
  }, [window.location.search]);

  const handleStatusUpdate = async (opportunityId: number, newStatus: string) => {
    try {
      await updateOpportunityStatus(opportunityId, newStatus);
      toast.success('Status updated successfully');
      fetchOpportunities(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error updating status:', error);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSelected([]); // Clear selection when changing page
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelected([]); // Clear selection when changing rows per page
  };
  
  const handleWorkspaceSelect = (selectedIds: string[]) => {
    const selected = contractWorkspaces.filter(ws => selectedIds.includes(ws.id));
    setSelectedWorkspaces(selected);
    setPage(0); // Reset to first page when changing filter
    
    // Update URL with selected contract IDs
    const newSearchParams = new URLSearchParams(window.location.search);
    if (selectedIds.length > 0) {
      newSearchParams.set('contract_ids', selectedIds.join(','));
    } else {
      newSearchParams.delete('contract_ids');
    }
    newSearchParams.delete('contract_id'); // Remove single contract_id if it exists
    const newURL = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.replaceState(null, '', newURL);
  };

  const handleViewDetails = (id: number) => {
    navigate(`/homepage/opportunities/${id}`);
  };

  const handleApprove = () => {
    if (selected.length === 0) {
      toast.warning('Please select opportunities to approve');
      return;
    }
    handleStatusUpdate(selected[0], 'approved');
    setSelected([]);
  };

  const handleReject = () => {
    if (selected.length === 0) {
      toast.warning('Please select opportunities to reject');
      return;
    }
    handleStatusUpdate(selected[0], 'rejected');
    setSelected([]);
  };

  const tableData = useMemo(() => {
    return opportunities.map(opp => ({
      ...opp,
      actions: (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(opp.id);
              }}
              size="small"
            >
              <img src={ViewIcon} alt="View Details" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }));
  }, [opportunities]);

  const OpportunitiesTable: React.FC<{
    data: Opportunity[];
    selected: number[];
    onSelect: (ids: number[]) => void;
  }> = ({ data, selected, onSelect }) => {
    return (
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={data.length > 0 && selected.length === data.length}
                  indeterminate={selected.length > 0 && selected.length < data.length}
                  onChange={(e) => onSelect(e.target.checked ? data.map(row => row.id) : [])}
                />
              </TableCell>
              <TableCell>Opportunity Name</TableCell>
              <TableCell>Client Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No opportunities found
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(row.id)}
                      onChange={(e) => {
                        const newSelected = e.target.checked
                          ? [...selected, row.id]
                          : selected.filter(id => id !== row.id);
                        onSelect(newSelected);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.opportunity_name}</TableCell>
                  <TableCell>{row.client_name}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.value}</TableCell>
                  <TableCell>{row.created_date}</TableCell>
                  <TableCell>{row.actions}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={{ mb: 3 }}
        />
      </TableContainer>
    );
  };

  return (
    <Grid container sx={{ width: "100%", justifyContent: "center" }}>
      <Grid
        sx={{
          flex: 1,
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#fff",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <Grid
          container
          sx={{ width: "100%" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            align="center"
            sx={{ fontSize: "1.15rem", px: 2, p: 1.5 }}
          >
            Contract Opportunities
          </Typography>
        </Grid>
        <Divider sx={{ width: "100%" }} />
        
        <Box sx={{ 
          display: "flex", 
          justifyContent: "flex-end", 
          width: "100%", 
          p: 2, 
          gap: 2 
        }}>
          <Button
            variant="outlined"
            sx={{
              fontSize: "0.875rem",
              width: 220,
              height: 38,
              borderRadius: "3px",
              background: "#FFF",
              color: "#8950FC",
              cursor: "pointer",
              borderColor: selected.length === 0 ? "rgba(0, 0, 0, 0.38)" : "#8950FC",
              '&:hover': {
                borderColor: "#8950FC",
                background: "#FFF",
              }
            }}
            onClick={handleApprove}
            disabled={selected.length === 0}
            startIcon={<img src={CheckMarkGreen} alt="Approve" style={{ width: 20, height: 20 }} />}
          >
            Approve
          </Button>
          <Button
            sx={{
              fontSize: "0.875rem",
              width: 200,
              height: 38,
              color: "#ffffff",
              backgroundColor: "#8950FC",
              cursor: "pointer",
              '&:hover': {
                backgroundColor: "#7339FB",
              },
              '&:disabled': {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.38)"
              }
            }}
            variant="contained"
            onClick={handleReject}
            disabled={selected.length === 0}
            startIcon={<img src={Cancel} alt="Reject" style={{ width: 20, height: 20 }} />}
          >
            Reject
          </Button>
        </Box>
        
        {/* Headline Stats */}
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={3}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Open Opportunities
                </Typography>
                <Typography variant="h5">
                  {stats.openOpportunities}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={3}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Closed Opportunities
                </Typography>
                <Typography variant="h5">
                  {stats.closedOpportunities}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={3}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Value Open Opps
                </Typography>
                <Typography variant="h5">
                  {stats.totalValueOpenOpps}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={3}>
            <StyledCard>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Value Recovered
                </Typography>
                <Typography variant="h5">
                  {stats.totalValueRecovered}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
        
        {/* Filter Section */}
        <Box sx={{ width: '100%', px: 2 }}>
          <Paper sx={{ 
            width: '100%',
            p: 2, 
            borderRadius: 1,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
            position: 'relative', // Ensure proper stacking context
            zIndex: 1 // Lower than dropdown menu but higher than other elements
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer'
            }} 
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              <Typography variant="subtitle1" fontWeight="medium" color="text.primary">
                Filters {selectedWorkspaces.length > 0 && `(${selectedWorkspaces.length} selected)`}
              </Typography>
              <IconButton size="small" sx={{ p: 0 }}>
                {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={filtersExpanded} timeout="auto">
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={4}>
                    <Box sx={{ position: 'relative', zIndex: 'auto' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Contract Workspace
                      </Typography>
                      <SearchableDropdown
                        options={contractWorkspaces}
                        selectedValues={selectedWorkspaces.map(ws => ws.id)}
                        onSelect={handleWorkspaceSelect}
                        multiSelect={true}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>
        </Box>

        <Box sx={{ width: '100%', p: 2 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="Open Opportunities" />
            <Tab label="Closed Opportunities" />
            <Tab label="Not Found" />
          </Tabs>
          <OpportunitiesTable
            data={tableData}
            selected={selected}
            onSelect={setSelected}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default Opportunities;
